import { supabase } from "../../../supabase";
import { openExternalUrl } from "../../../helpers/openExternalUrl";
import { getConnectedServiceStatus } from "../../connectedServicesApi";
import { SERVICE_GMAIL } from "../../registry";
import {
  formatAllUnreadSenders,
  formatEmailSenderOverview,
  formatSpamAttentionIntro,
} from "../formatEmailVoiceSummary";
import {
  EMAIL_NOT_CONNECTED_MESSAGE,
  EMAIL_OPENING_MESSAGE,
  EMAIL_OPENING_SPAM_MESSAGE,
  EMAIL_UNAVAILABLE_MESSAGE,
} from "../emailProviderTypes";

/** Gmail web inbox (web / TWA Custom Tabs). */
export const GMAIL_INBOX_URL = "https://mail.google.com/mail/u/0/#inbox";

/** Gmail Spam folder. */
export const GMAIL_SPAM_URL = "https://mail.google.com/mail/u/0/#spam";

export function buildGmailMessageUrl(messageId) {
  const id = String(messageId || "").trim();
  if (!id) {
    return GMAIL_INBOX_URL;
  }
  return `https://mail.google.com/mail/u/0/#inbox/${encodeURIComponent(id)}`;
}

function mapNotConnectedOrUnavailable(data, error) {
  const code = data?.code || "email_summary_failed";
  if (
    code === "email_not_connected" ||
    code === "gmail_not_connected" ||
    code === "email_reauth_required" ||
    code === "gmail_reauth_required"
  ) {
    return {
      success: false,
      code: "email_not_connected",
      message: EMAIL_NOT_CONNECTED_MESSAGE,
      providerId: SERVICE_GMAIL,
    };
  }
  return {
    success: false,
    code: error ? "email_summary_failed" : code,
    message: EMAIL_UNAVAILABLE_MESSAGE,
    providerId: SERVICE_GMAIL,
  };
}

/**
 * Gmail implementation of the EmailProvider contract.
 * Ashley must not import this module — use EmailService instead.
 * @returns {import("../emailProviderTypes").EmailProvider}
 */
export function createGmailProvider() {
  return {
    id: SERVICE_GMAIL,
    label: "Gmail",

    async isConnected() {
      const status = await getConnectedServiceStatus(SERVICE_GMAIL);
      return Boolean(status.connected);
    },

    async summarizeRecent(options = {}) {
      const hours = options.hours ?? 72;

      try {
        const connected = await this.isConnected();
        if (!connected) {
          return {
            success: false,
            code: "email_not_connected",
            message: EMAIL_NOT_CONNECTED_MESSAGE,
            providerId: SERVICE_GMAIL,
          };
        }

        const { data, error } = await supabase.functions.invoke("email-summary", {
          body: { hours, mode: "primary" },
        });

        if (error || data?.error) {
          return mapNotConnectedOrUnavailable(data, error);
        }

        const messages = Array.isArray(data?.messages) ? data.messages : [];
        const spamUnreadCount =
          typeof data?.spamUnreadCount === "number" ? data.spamUnreadCount : 0;
        // Always format on the client so browse prompts stay in sync with V1.5.
        const overview = formatEmailSenderOverview(messages, {
          spamReview: { unreadCount: spamUnreadCount },
        });
        const count = typeof data?.count === "number" ? data.count : messages.length;

        return {
          success: true,
          message: overview,
          count,
          messages,
          providerId: SERVICE_GMAIL,
        };
      } catch {
        return {
          success: false,
          code: "email_summary_failed",
          message: EMAIL_UNAVAILABLE_MESSAGE,
          providerId: SERVICE_GMAIL,
        };
      }
    },

    async getMessageDetail(messageId) {
      try {
        const connected = await this.isConnected();
        if (!connected) {
          return {
            success: false,
            code: "email_not_connected",
            message: EMAIL_NOT_CONNECTED_MESSAGE,
            providerId: SERVICE_GMAIL,
          };
        }

        const id = String(messageId || "").trim();
        if (!id) {
          return {
            success: false,
            code: "email_message_id_required",
            message: EMAIL_UNAVAILABLE_MESSAGE,
            providerId: SERVICE_GMAIL,
          };
        }

        const { data, error } = await supabase.functions.invoke("email-summary", {
          body: { mode: "message_detail", messageId: id },
        });

        if (error || data?.error) {
          return mapNotConnectedOrUnavailable(data, error);
        }

        const messages = Array.isArray(data?.messages) ? data.messages : [];
        const bodyText =
          typeof data?.bodyText === "string"
            ? data.bodyText
            : messages[0]?.bodyText || "";

        return {
          success: true,
          message: "",
          count: 1,
          messages,
          bodyText,
          providerId: SERVICE_GMAIL,
        };
      } catch {
        return {
          success: false,
          code: "email_summary_failed",
          message: EMAIL_UNAVAILABLE_MESSAGE,
          providerId: SERVICE_GMAIL,
        };
      }
    },

    async openMessage(messageId) {
      try {
        const connected = await this.isConnected();
        if (!connected) {
          return {
            success: false,
            code: "email_not_connected",
            message: EMAIL_NOT_CONNECTED_MESSAGE,
            providerId: SERVICE_GMAIL,
          };
        }

        const url = buildGmailMessageUrl(messageId);
        return {
          success: true,
          message: EMAIL_OPENING_MESSAGE,
          providerId: SERVICE_GMAIL,
          performAction: () => {
            openExternalUrl(url);
          },
        };
      } catch {
        return {
          success: false,
          code: "email_open_failed",
          message: EMAIL_UNAVAILABLE_MESSAGE,
          providerId: SERVICE_GMAIL,
        };
      }
    },

    async reviewAllUnread(options = {}) {
      const hours = options.hours ?? 72;

      try {
        const connected = await this.isConnected();
        if (!connected) {
          return {
            success: false,
            code: "email_not_connected",
            message: EMAIL_NOT_CONNECTED_MESSAGE,
            providerId: SERVICE_GMAIL,
          };
        }

        const { data, error } = await supabase.functions.invoke("email-summary", {
          body: { hours, mode: "primary" },
        });

        if (error || data?.error) {
          return mapNotConnectedOrUnavailable(data, error);
        }

        const messages = Array.isArray(data?.messages) ? data.messages : [];
        const count = typeof data?.count === "number" ? data.count : messages.length;

        return {
          success: true,
          message: formatAllUnreadSenders(messages),
          count,
          messages,
          providerId: SERVICE_GMAIL,
        };
      } catch {
        return {
          success: false,
          code: "email_summary_failed",
          message: EMAIL_UNAVAILABLE_MESSAGE,
          providerId: SERVICE_GMAIL,
        };
      }
    },

    async checkSpamAttention() {
      try {
        const connected = await this.isConnected();
        if (!connected) {
          return {
            success: false,
            code: "email_not_connected",
            message: EMAIL_NOT_CONNECTED_MESSAGE,
            providerId: SERVICE_GMAIL,
          };
        }

        const { data, error } = await supabase.functions.invoke("email-summary", {
          body: { mode: "spam_attention" },
        });

        if (error || data?.error) {
          return mapNotConnectedOrUnavailable(data, error);
        }

        const messages = Array.isArray(data?.messages) ? data.messages : [];
        const flaggedCount =
          typeof data?.flaggedCount === "number" ? data.flaggedCount : messages.length;

        return {
          success: true,
          message: formatSpamAttentionIntro(flaggedCount),
          count: flaggedCount,
          messages,
          providerId: SERVICE_GMAIL,
        };
      } catch {
        return {
          success: false,
          code: "email_summary_failed",
          message: EMAIL_UNAVAILABLE_MESSAGE,
          providerId: SERVICE_GMAIL,
        };
      }
    },

    async openInbox() {
      try {
        const connected = await this.isConnected();
        if (!connected) {
          return {
            success: false,
            code: "email_not_connected",
            message: EMAIL_NOT_CONNECTED_MESSAGE,
            providerId: SERVICE_GMAIL,
          };
        }

        return {
          success: true,
          message: EMAIL_OPENING_MESSAGE,
          providerId: SERVICE_GMAIL,
          performAction: () => {
            openExternalUrl(GMAIL_INBOX_URL);
          },
        };
      } catch {
        return {
          success: false,
          code: "email_open_failed",
          message: EMAIL_UNAVAILABLE_MESSAGE,
          providerId: SERVICE_GMAIL,
        };
      }
    },

    async openSpam() {
      try {
        const connected = await this.isConnected();
        if (!connected) {
          return {
            success: false,
            code: "email_not_connected",
            message: EMAIL_NOT_CONNECTED_MESSAGE,
            providerId: SERVICE_GMAIL,
          };
        }

        return {
          success: true,
          message: EMAIL_OPENING_SPAM_MESSAGE,
          providerId: SERVICE_GMAIL,
          performAction: () => {
            openExternalUrl(GMAIL_SPAM_URL);
          },
        };
      } catch {
        return {
          success: false,
          code: "email_open_failed",
          message: EMAIL_UNAVAILABLE_MESSAGE,
          providerId: SERVICE_GMAIL,
        };
      }
    },
  };
}
